import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import BackButton from "../components/BackButton";
import BackToTop from "../components/BackToTop";
import hljs from "../vendor/js/hljs-setup";
import { useEffect } from "react";


export default function FCMPushNotifications() {
    useEffect(() => {
        hljs.highlightAll();
    }, []);

    const nameExt = " | charlie-gp";
    const [postTitle, postDate, readTime] = [
        "FCM Push Notifications: Android, React Native & Rails",
        "Jan. 30, 2026",
        "5min. read",
    ];

    return (
        <div id="posts">
            <Helmet>
                <title>{postTitle + nameExt}</title>
            </Helmet>
            <BackButton />

            <div id="post-date">
                <small>
                    {postDate} &nbsp;&bull;&nbsp; {readTime} &nbsp;&bull;&nbsp;{" "}
                    <Link to="/">all posts</Link>
                </small>
            </div>

            <h1 id="post-title">{postTitle}</h1>

            <p className="lead">
                Push notifications are powerful tools for driving user engagement. Implementing a cross-platform system involves coordinating the Firebase Cloud Messaging (FCM) service, a React Native frontend, and a Rails backend.
            </p>

            <h2 className="post-sect">Front-end: React Native</h2>
            <p>
                We use <code>@react-native-firebase/messaging</code> to handle FCM tokens and background messages.
            </p>

            <h3 className="post-sect-sub">Getting the FCM Token</h3>
            <pre>
                <code className="language-javascript">
                    {`import messaging from '@react-native-firebase/messaging';

async function requestUserPermission() {
  const authStatus = await messaging().requestPermission();
  const enabled =
    authStatus === messaging.AuthorizationStatus.AUTHORIZED ||
    authStatus === messaging.AuthorizationStatus.PROVISIONAL;

  if (enabled) {
    const fcmToken = await messaging().getToken();
    console.log('FCM Token:', fcmToken);
    // Send this token to Rails backend to store
    await api.post('/users/update_fcm_token', { token: fcmToken });
  }
}`}
                </code>
            </pre>

            <h2 className="post-sect">Back-end: Rails & Sidekiq</h2>
            <p>
                The Rails backend is responsible for storing the tokens and triggering notifications via the FCM HTTP v1 API.
            </p>

            <h3 className="post-sect-sub">The Notification Worker</h3>
            <p>
                Always send notifications in the background to avoid blocking the main thread.
            </p>
            <pre>
                <code className="language-ruby">
                    {`# app/sidekiq/fcm_notification_worker.rb
class FcmNotificationWorker
  include Sidekiq::Job

  def perform(user_id, title, body)
    user = User.find(user_id)
    return unless user.fcm_token.present?

    fcm = FCM.new(ENV['FCM_SERVER_KEY'])
    options = {
      notification: {
        title: title,
        body: body,
        sound: 'default'
      },
      data: { 
        type: 'new_message',
        id: '123'
      }
    }
    
    response = fcm.send([user.fcm_token], options)
    Rails.logger.info "FCM Response: #{response}"
  end
end`}
                </code>
            </pre>

            <h2 className="post-sect">Android Configuration</h2>
            <p>
                For Android, ensure you have the <code>google-services.json</code> placed in your <code>android/app</code> directory and the Google Services plugin configured in your <code>build.gradle</code> files.
            </p>

            <h2 className="post-sect">Summary</h2>
            <p>
                The flow is simple: React Native obtains the token and sends it to Rails. Rails stores the token and uses Sidekiq to send requests to FCM whenever a notification is needed. FCM then handles the heavy lifting of delivery to the physical device.
            </p>

            <BackToTop />

            <div className="mt-5">
                <BackButton />
            </div>
        </div>
    );
}
